import React, { useState, useEffect, useContext, useMemo } from "react";
import { Button, Form } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, gql } from "@apollo/client";
import Spinner from "../../components/Spinner";
import StarekContext from "../../context/StarekContext";

import AddCycleOffCanvasWizard from "../../components/Wizards/AddCycleOffCanvasWizard";
import CycleStageListView from "../../components/CycleStageListView";

import "../../starek.css";

const Cycles = () => {
    const starekContext = useContext(StarekContext);
    const [deleteDone, setDeleteDone] = useState(0);

    const GET_CYCLES_LIST_FOR_PLANT = gql`
        query GetCycles($plantId: Int!) {
            cycles(where: { plant_id: { equals: $plantId } }) {
                id
                name
                description
                created_at
                cycle_stages {
                    id
                    name
                    is_productive
                    duration
                }
            }
        }
    `;

    const DELETE_ONE_CYCLE = gql`
        mutation deleteOneCycle($id: Int!) {
            deleteOneCycle(where: { id: $id }) {
                id
            }
        }
    `;

    const { data, loading, refetch } = useQuery(GET_CYCLES_LIST_FOR_PLANT, {
        client: starekContext.engineClient,
        variables: { plantId: starekContext.lastUsedPlant.id },
    });

    const [deleteOneCycle] = useMutation(DELETE_ONE_CYCLE, {
        client: starekContext.engineClient,
    });

    const [items, setItems] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);
    const [deleting, setDeleting] = useState(false);

    const [showAddCycleDrawer, setShowAddCycleDrawer] = useState(false);
    const handleAddCycleDrawerClose = () => setShowAddCycleDrawer(false);
    const handleAddCycleDrawerShow = () => setShowAddCycleDrawer(true);

    useEffect(() => {
        if (!data) return;
        setItems(data.cycles);
    }, [data]);

    const filteredItems = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (c) =>
                c.name?.toLowerCase().includes(q) ||
                c.description?.toLowerCase().includes(q)
        );
    }, [items, search]);

    const toggleSelection = (id, checked) => {
        setSelectedIds((prev) =>
            checked
                ? Array.from(new Set([...prev, id]))
                : prev.filter((x) => x !== id)
        );
    };

    const canEdit = selectedIds.length === 1;
    const canDelete = selectedIds.length >= 1;

    const handleDelete = async () => {
        if (!canDelete || deleting) return;
        setDeleting(true);
        setDeleteDone(0);
        const total = selectedIds.length;

        try {
            for (const id of selectedIds) {
                try {
                    await deleteOneCycle({ variables: { id } });
                } catch (err) {
                    starekContext?.showError?.(err);
                } finally {
                    setDeleteDone((d) => d + 1);
                }
            }
            setSelectedIds([]);
            await refetch();
        } finally {
            setDeleting(false);
            setDeleteDone(0);
        }
    };

    const editingItem = canEdit
        ? items.find((i) => i.id === selectedIds[0])
        : null;

    return loading ? (
        <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ minHeight: "100vh" }}
        >
            <div className="text-xs d-flex justify-content-center">
                <i className="fa fa-recycle fa-10x text-success"></i>
            </div>
            <br />
            <Spinner />
        </div>
    ) : (
        <div className="container-fluid">
            {items && (
                <>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <Form className="tw-w-[40%] tw-p-2">
                            <Form.Control
                                type="text"
                                placeholder="Search cycles by name or description…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </Form>

                        <div className="d-flex align-items-center">
                            <div className="me-3">
                                <span className="bg-danger rounded-circle px-9px me-2"></span>
                                Non-productive
                            </div>
                            <div className="me-3">
                                <span className="bg-success rounded-circle px-9px me-2"></span>
                                Productive
                            </div>

                            {selectedIds.length === 0 ? (
                                <>
                                    <Button
                                        variant="danger"
                                        className="me-2 d-flex align-items-center"
                                        disabled={!canDelete || deleting}
                                        onClick={handleDelete}
                                    >
                                        {deleting ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                />
                                                Deleting {deleteDone}/
                                                {selectedIds.length}
                                            </>
                                        ) : (
                                            "Delete Cycle"
                                        )}
                                    </Button>

                                    <div className="position-relative">
                                        <Button
                                            variant="secondary"
                                            disabled={!canEdit}
                                            title={
                                                canEdit
                                                    ? ""
                                                    : "Only single selection can be edited"
                                            }
                                            onClick={() =>
                                                setShowAddCycleDrawer(true)
                                            }
                                        >
                                            Edit Cycle
                                        </Button>
                                    </div>
                                    <Button
                                        variant="primary"
                                        onClick={handleAddCycleDrawerShow}
                                        className="tw-ml-2"
                                    >
                                        Add a Cycle
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        variant="danger"
                                        className="me-2 d-flex align-items-center"
                                        disabled={!canDelete || deleting}
                                        onClick={handleDelete}
                                    >
                                        {deleting ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                />
                                                Deleting {deleteDone}/
                                                {selectedIds.length}
                                            </>
                                        ) : (
                                            "Delete Cycle"
                                        )}
                                    </Button>

                                    <div className="position-relative">
                                        <Button
                                            variant="secondary"
                                            disabled={!canEdit}
                                            title={
                                                canEdit
                                                    ? ""
                                                    : "Only single selection can be edited"
                                            }
                                            onClick={() =>
                                                setShowAddCycleDrawer(true)
                                            }
                                        >
                                            Edit Cycle
                                        </Button>
                                    </div>
                                </>
                            )}

                            <AddCycleOffCanvasWizard
                                show={showAddCycleDrawer}
                                closeOffcanvas={() =>
                                    setShowAddCycleDrawer(false)
                                }
                                handleClose={handleAddCycleDrawerClose}
                                starekContext={starekContext}
                                item={editingItem || ""}
                                refetch={refetch}
                            />
                        </div>
                    </div>

                    <CycleStageListView
                        items={filteredItems}
                        selectedIds={selectedIds}
                        onToggle={toggleSelection}
                    />
                </>
            )}
        </div>
    );
};

export { Cycles };
